<template>
  <div>
    <PageTitle name="bulk-renewal"></PageTitle>

    <FeatureDisabledAlert v-if="!$tenant.supportsRenewal()"></FeatureDisabledAlert>

    <Spinner v-else-if="subscriptionsLoading"></Spinner>
    <form v-else-if="auth.isFullyLoggedIn" @submit.prevent="submitRecom">
      <div v-if="subscriptions">
        <div v-for="subscription in subscriptions" class="mb-10">
          <template v-for="businessPartner in subscription.businessPartners">
            <template v-if="businessPartner.role === 'WE'">
              <SubscriptionDetails class="mt-4" :subscription="subscription" :business-partner="businessPartner" :load-recommendations="true" @update:selectedRecommendation="selectedRecommendations[(subscription.clientId+subscription.orderNumber as unknown) as number] = $event" />
            </template>
          </template>
        </div>

        <Alert
          v-if="pwError"
          variant="error"
          data-testid="pw-error"
        >
          {{ pwError }}
        </Alert>
        <ButtonBar class="mt-4">
          <ButtonWithLoadingIndicator
            class="submit btn btn-primary"
            type="submit"
            :disabled="submitting || noSelectedRecommendation"
            :loading="submitting"
          >
            {{ $t("subscription-renewal.bulk-submit") }}
          </ButtonWithLoadingIndicator>
        </ButtonBar>
      </div>
    </form>

    <!-- AUTH -->
    <form v-else @submit.prevent="submitAuth">
      <Alert
        v-if="authError"
        variant="error"
        data-testid="auth-login-error"
      >
        {{ $t("auth.login-error") }}
      </Alert>

      <FormInput
        v-model="formData.email"
        type="email"
        :label="$t('business-partner.email.label')"
        max-length="35"
        required
      ></FormInput>
      <i18n-t
        tag="p"
        keypath="renewal.email-hint"
        class="mt-2 text-gray-700 text-xs"
      >
        <template #contactForm>
          <router-link to="/contact" class="link"
          >{{ $t("app.contact-form") }}
          </router-link>
        </template>
      </i18n-t>

      <FormInput
        v-model="formData.lastName"
        :label="$t('business-partner.last-name.label')"
        max-length="35"
        required
      ></FormInput>

      <FormInput
        v-model="formData.zipCode"
        :label="$t('business-partner.zipcode.label')"
        min-length="4"
        max-length="10"
        required
      ></FormInput>

      <ButtonBar class="mt-6">
        <ButtonWithLoadingIndicator
          type="submit"
          :loading="submitting"
          class="btn btn-primary font-semibold"
        >
          {{ $t("app.continue") }}
        </ButtonWithLoadingIndicator>
      </ButtonBar>
    </form>

    <AlertDialog
      ref="success"
      name="success"
      :negative-text="$t('app.close')"
    >
      {{ $t("subscription-renewal.success") }}
    </AlertDialog>
    <PopupWindow
      ref="popup"
      v-if="showPopup"
      :url="popupWindowUrl"
      :placeholder="$t('subscription-renewal.please-wait')"
      :title="$t('subscription-renewal.popup-window-title')"
      with-overlay
      target="bluk-renewal"
      @message="handlePopupMessage"
      @close="handlePopupClose"
    ></PopupWindow>
    <Overlay :show="waitingForPayment">
      <div class="text-white text-center max-w-xl p-4 z-20">
        <Spinner></Spinner>
        <p class="text-lg">{{$t('subscription-renewal.payment-processing')}}</p>
        <p class="text-xs mt-2">{{$t('subscription-renewal.payment-taking-too-long')}}
          <button type="button" class="underline inline-block" @click="stopWaitingForPayment">{{ $t('app.close') }}</button>
        </p>
      </div>
    </Overlay>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRaw } from 'vue'
import PageTitle from "@/components/PageTitle.vue";
import Spinner from "@/components/Spinner.vue";
import ButtonBar from "@/components/ButtonBar.vue";
import ButtonWithLoadingIndicator from "@/components/ButtonWithLoadingIndicator.vue";
import FormInput from "@/components/form/FormInput.vue";
import FeatureDisabledAlert from "@/components/FeatureDisabledAlert.vue";
import SubscriptionDetails from "@/components/SubscriptionDetails.vue";
import Alert from "@/components/Alert.vue";
import AlertDialog from "@/components/AlertDialog.vue";
import PopupWindow from "@/components/PopupWindow.vue";
import Overlay from "@/components/Overlay.vue";

import { useAuth } from "@/pinia/auth";
import { useOrders } from "@/pinia/orders";
import { useRoute } from "vue-router";
import { useAxios } from "@/plugins/axios";

import { Recommendation } from "@/models/recommendation";
import { AcceptAllRecommendationsResult } from "@/models/acceptAllRecommendationsResult";
import { useVfm } from "@/plugins/vfm";
import { PendingPayment } from "@/models/pendingPayment";
// import CrossSellingOfferModal from "@/components/CrossSellingOfferModal.vue";


const auth = useAuth()
const orders = useOrders()
const route = useRoute()
const axios = useAxios()
const vfm = useVfm()

const submitting = ref(false)
const formData = ref<{email: string | null, lastName: string, zipCode: string | null}>({
  email: route.query.email as string ?? null,
  lastName: route.query.lastName1 as string ?? '',
  zipCode: route.query.zipCode as string ?? null,
})

const authError = ref(null)
const selectedRecommendations = ref<Recommendation[] | string[]>([])

const pwError = ref(null)
const showPopup = ref(false)
const popupWindowUrl = ref("")
const showSuccessOverlay = ref(false)
const waitingForPayment = ref(false)
const interval = ref<number | null>(null)
const acceptAllRecommendationResult = ref<AcceptAllRecommendationsResult | null>(null)

const subscriptions = computed(() => orders.subscriptions)
const subscriptionsLoading = computed(() => orders.loading)

const noSelectedRecommendation = computed(() => {
  for (const key in selectedRecommendations.value) {
    if (selectedRecommendations.value[key] !== '-') {
      return false;
    }
  }
  return true;
})

async function submitRecom() {
  try {
    pwError.value = null;
    submitting.value = true;
    showPopup.value = true;

    let payload: any = {};
    for (const clientOrderNumber in selectedRecommendations.value) {
      if (selectedRecommendations.value[clientOrderNumber] !== '-') {
        payload[clientOrderNumber] = selectedRecommendations.value[clientOrderNumber];
      }
    }

    // hard coded error message
    // check cybersource/direct_debit payment providers
    let selectedPaymentProviders: any = {};
    for (const key in payload) {
      let provider = payload[key].paymentProviders[0].PAYMENT_PROVIDER;
      selectedPaymentProviders[provider] = provider;
    }
    if (selectedPaymentProviders.cybersource && selectedPaymentProviders.direct_debit) {
      throw new Error('The payment methods "Credit Card" and "Direct Debit" cannot be used together.')
    }

    const response = await axios.post<AcceptAllRecommendationsResult>('/subscriptions/acceptAllRecommendations', {
      selectedRecommendations: payload,
    });

    const data = response.data;

    // TODO: what if showPopup is false but the service returns an url?
    if (showPopup && data.url) {
      acceptAllRecommendationResult.value = data
      popupWindowUrl.value = data.url;
    } else {
      showSuccess();
    }

  } catch (e: any) {
    if (e.response && e.response.data) {
      pwError.value = e.response.data;
      console.log(e.response.data);
    } else {
      pwError.value = e;
      console.log(e);
    }
    handlePopupClose();
  } finally {
    submitting.value = false;
  }
}

function showSuccess() {
  if (auth.isFullyLoggedIn) {
    vfm.show("success");
  } else {
    // show an overlay instead
    showSuccessOverlay.value = true
  }
}

function handlePopupMessage() {
  showPopup.value = false;
  popupWindowUrl.value = "";

  // if (acceptARecommendationResult.value?.crossSelling) {
    // when cross selling is enabled, we wait for a successful payment
    // waitForPayment()
  // } else {
    showSuccess();
  // }
}

function handlePopupClose() {
  showPopup.value = false;
  popupWindowUrl.value = "";
}


async function checkPaymentStatus() {
  try {
    const paymentId = acceptAllRecommendationResult.value!.pendingPaymentId
    const subscriptionId = 'b'+subscriptions.value?.at(0)?.id;

    const response = await axios.get<PendingPayment>(`/subscriptions/${subscriptionId}/pending-payments/${paymentId}`,)

    if (response.data.status === 'pending') {
      return
    }

    if (response.data.status === 'success') {
      // if (response.data.crossSellingOffer !== null) {
      //   vfm.show({
      //     component: CrossSellingOfferModal,
      //     bind: {
      //       subscription: subscription.value,
      //       offer: response.data.crossSellingOffer
      //     }
      //   })
      // } else {
        showSuccess()
      // }
    } else if (response.data.status === 'error') {
      vfm.show('paymentError')
    } else if (response.data.status === 'canceled') {
      vfm.show('paymentCanceled')
    }
    stopWaitingForPayment()
  } catch (error) {
    // TODO
  }
}

function waitForPayment() {
  waitingForPayment.value = true
  interval.value = setInterval(checkPaymentStatus, 1000) as unknown as number
}

function stopWaitingForPayment() {
  waitingForPayment.value = false
  if (interval.value) {
    clearInterval(interval.value)
  }
}



async function submitAuth() {
  try {
    authError.value = null;
    submitting.value = true;

    await auth.loginUsingEmailZip(formData.value);
    if (auth.isFullyLoggedIn) {
      orders.fetch()
    }
  } catch (e: any) {
    authError.value = e;

    if (e.response) {
      console.log(e.response.data);
    } else {
      console.log(e);
    }
  } finally {
    submitting.value = false;
  }
}

if (auth.isFullyLoggedIn) {
  orders.fetch()
} else if (formData.value.email && formData.value.lastName && formData.value.zipCode) {
  submitAuth()
}
</script>
